/**
 * Industry Template Selector Component
 * 
 * Allows users to select from predefined industry templates to create
 * multiple content buckets at once with appropriate names and settings.
 * 
 * Features:
 * - Visual template cards with descriptions
 * - Preview of buckets that will be created
 * - Bulk creation with confirmation
 * - Mobile-responsive design
 * 
 * @author AI Assistant
 * @created 2024
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  FitnessCenter as FitnessIcon,
  Restaurant as FoodIcon,
  ShoppingCart as EcommerceIcon,
  Home as RealEstateIcon,
  Computer as TechIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { useContentBuckets } from '../providers/ContentBucketsProvider';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 600,
    maxWidth: 800,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      maxWidth: '95vw',
      margin: theme.spacing(1)
    }
  }
}));

const TemplateCard = styled(Card)(({ theme, selected }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4]
  }
}));

const TemplateIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  '& .MuiSvgIcon-root': {
    fontSize: '3rem',
    color: theme.palette.primary.main
  }
}));

const BucketPreview = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginTop: theme.spacing(2)
}));

// Template definitions
const TEMPLATES = [
  {
    id: 'business_coach',
    name: 'Business Coach',
    icon: <BusinessIcon />,
    description: 'Perfect for business coaches, consultants, and professional service providers',
    buckets: [
      'Behind the Scenes',
      'Client Success Stories',
      'Educational Content',
      'Personal Branding',
      'Industry Insights',
      'Motivational Content'
    ]
  },
  {
    id: 'fitness_creator',
    name: 'Fitness Creator',
    icon: <FitnessIcon />,
    description: 'Ideal for fitness trainers, nutritionists, and wellness coaches',
    buckets: [
      'Workout Videos',
      'Nutrition Tips',
      'Transformation Stories',
      'Motivational Content',
      'Exercise Tutorials',
      'Healthy Recipes'
    ]
  },
  {
    id: 'food_brand',
    name: 'Food Brand',
    icon: <FoodIcon />,
    description: 'Great for restaurants, food bloggers, and culinary businesses',
    buckets: [
      'Recipe Content',
      'Behind the Kitchen',
      'Customer Features',
      'Seasonal Specials',
      'Cooking Tips',
      'Food Photography'
    ]
  },
  {
    id: 'ecommerce',
    name: 'E-commerce',
    icon: <EcommerceIcon />,
    description: 'Designed for online stores and product-based businesses',
    buckets: [
      'Product Showcases',
      'Customer Reviews',
      'Behind the Brand',
      'How-to Guides',
      'User Generated Content',
      'Sales & Promotions'
    ]
  },
  {
    id: 'real_estate',
    name: 'Real Estate',
    icon: <RealEstateIcon />,
    description: 'Perfect for real estate agents and property professionals',
    buckets: [
      'Property Listings',
      'Market Updates',
      'Client Testimonials',
      'Neighborhood Spotlights',
      'Home Buying Tips',
      'Success Stories'
    ]
  },
  {
    id: 'tech_startup',
    name: 'Tech Startup',
    icon: <TechIcon />,
    description: 'Ideal for technology companies and SaaS businesses',
    buckets: [
      'Product Updates',
      'Company Culture',
      'Industry News',
      'Customer Success',
      'Technical Content',
      'Team Highlights'
    ]
  }
];

const IndustryTemplateSelector = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { createFromTemplate, loading, error, clearError } = useContentBuckets();

  // Local state
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedTemplate('');
      clearError();
    }
  }, [open, clearError]);

  // Get selected template data
  const selectedTemplateData = TEMPLATES.find(t => t.id === selectedTemplate);

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
  };

  // Handle create buckets
  const handleCreateBuckets = async () => {
    if (!selectedTemplate) return;

    setIsCreating(true);
    clearError();

    try {
      await createFromTemplate({
        industry_template: selectedTemplate
      });
      onSuccess?.();
    } catch (err) {
      console.error('Failed to create buckets from template:', err);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!isCreating) {
      onClose();
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="h2">
          Choose Industry Template
        </Typography>
        <Button
          onClick={handleClose}
          disabled={isCreating}
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent dividers>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Select a template that matches your industry to get started with pre-configured content buckets.
        </Typography>

        {/* Template Selection */}
        <RadioGroup
          value={selectedTemplate}
          onChange={(e) => handleTemplateSelect(e.target.value)}
        >
          <Grid container spacing={3}>
            {TEMPLATES.map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <FormControlLabel
                  value={template.id}
                  control={<Radio sx={{ display: 'none' }} />}
                  label=""
                  sx={{ margin: 0, width: '100%' }}
                />
                <TemplateCard
                  selected={selectedTemplate === template.id}
                  onClick={() => handleTemplateSelect(template.id)}
                  elevation={selectedTemplate === template.id ? 4 : 1}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <TemplateIcon>
                      {template.icon}
                    </TemplateIcon>
                    <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                      {template.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {template.description}
                    </Typography>
                    <Chip
                      label={`${template.buckets.length} buckets`}
                      size="small"
                      color={selectedTemplate === template.id ? 'primary' : 'default'}
                    />
                    {selectedTemplate === template.id && (
                      <Box sx={{ mt: 2 }}>
                        <CheckIcon color="primary" />
                      </Box>
                    )}
                  </CardContent>
                </TemplateCard>
              </Grid>
            ))}
          </Grid>
        </RadioGroup>

        {/* Bucket Preview */}
        {selectedTemplateData && (
          <BucketPreview>
            <Typography variant="h6" gutterBottom>
              Buckets that will be created:
            </Typography>
            <Grid container spacing={1}>
              {selectedTemplateData.buckets.map((bucketName, index) => (
                <Grid item key={index}>
                  <Chip
                    label={bucketName}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              You can customize these buckets after creation.
            </Typography>
          </BucketPreview>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isCreating}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateBuckets}
          variant="contained"
          disabled={!selectedTemplate || isCreating}
          startIcon={isCreating ? <CircularProgress size={20} /> : null}
        >
          {isCreating 
            ? 'Creating Buckets...' 
            : `Create ${selectedTemplateData?.buckets.length || 0} Buckets`
          }
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default IndustryTemplateSelector; 